import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';

/**
 * The CMS content hierarchy is
 * Language → Course → Learning Path → Theme → Module → Activity → Question.
 *
 * Each drill-down level gets its own URL keyed on the entity id rather than being held in
 * component state, so a deep link, a refresh and the browser Back button all behave. Each screen
 * resolves its own ancestors for the breadcrumb.
 *
 * Learning paths have no standalone route: the design nests them in an accordion on the course
 * page, with their themes as cards inside each open path. Themes *do* get a route, because their
 * modules need a screen of their own to hang activities off.
 */
export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/auth/login/login.component').then((m) => m.LoginComponent),
  },
  {
    // Where an invite email lands: `/accept-invite?token=…`, the URL the API's `inviteUrl()` builds.
    // Outside `authGuard` on purpose — the invitee has no session, and obtaining one is the point.
    path: 'accept-invite',
    loadComponent: () =>
      import('./features/auth/accept-invite/accept-invite.component').then(
        (m) => m.AcceptInviteComponent,
      ),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./core/components/admin-shell.component').then((m) => m.AdminShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
      },

      // ── Content hierarchy ──
      {
        path: 'languages',
        loadComponent: () => import('./features/languages/languages.component').then((m) => m.LanguagesComponent),
      },
      {
        // `?language=<id>` narrows the grid to one language; unfiltered lists every course.
        path: 'courses',
        loadComponent: () => import('./features/courses/courses.component').then((m) => m.CoursesComponent),
      },
      {
        path: 'courses/:courseId',
        loadComponent: () =>
          import('./features/course-detail/course-detail.component').then((m) => m.CourseDetailComponent),
      },
      {
        path: 'themes/:themeId',
        loadComponent: () =>
          import('./features/theme-detail/theme-detail.component').then((m) => m.ThemeDetailComponent),
      },
      {
        path: 'modules/:moduleId',
        loadComponent: () => import('./features/activities/activities.component').then((m) => m.ActivitiesComponent),
      },
      {
        path: 'activities/:activityId',
        loadComponent: () => import('./features/questions/questions.component').then((m) => m.QuestionsComponent),
      },
      // Authoring a question is a screen of its own, not a modal over the list — the forms are the
      // longest in the CMS, and an author part-way through a set expects back, refresh and a
      // pasteable link to work. `new` sits before `:questionId` so it is not read as an id.
      {
        path: 'activities/:activityId/questions/new',
        loadComponent: () =>
          import('./features/questions/question-builder-page/question-builder-page.component').then(
            (m) => m.QuestionBuilderPageComponent,
          ),
      },
      {
        path: 'activities/:activityId/questions/:questionId',
        loadComponent: () =>
          import('./features/questions/question-builder-page/question-builder-page.component').then(
            (m) => m.QuestionBuilderPageComponent,
          ),
      },

      // ── Supporting sections ──
      {
        path: 'lexicon',
        loadComponent: () => import('./features/lexicon/lexicon.component').then((m) => m.LexiconComponent),
      },
      {
        path: 'gamification',
        loadComponent: () =>
          import('./features/gamification/gamification.component').then((m) => m.GamificationComponent),
      },
      {
        path: 'media',
        loadComponent: () => import('./features/media/media.component').then((m) => m.MediaComponent),
      },
      {
        path: 'publishing',
        loadComponent: () => import('./features/publishing/publishing.component').then((m) => m.PublishingComponent),
      },
      {
        // App feedback (ADR-0056) — read-only, `?rating=` and `?q=` are held in component state.
        path: 'feedback',
        loadComponent: () => import('./features/feedback/feedback.component').then((m) => m.FeedbackComponent),
      },
      {
        path: 'admins',
        loadComponent: () => import('./features/users/users.component').then((m) => m.UsersComponent),
      },

      // Learners is in the primary nav (ADR-0056); its stats page is reached from a row's link.
      {
        path: 'learners',
        loadComponent: () => import('./features/learners/learners.component').then((m) => m.LearnersComponent),
      },
      {
        path: 'learners/:accountRef',
        loadComponent: () =>
          import('./features/learner-stats/learner-stats.component').then((m) => m.LearnerStatsComponent),
      },

      // ── Reachable by URL and from the dashboard, but not in the primary nav ──
      {
        path: 'xp-events',
        loadComponent: () => import('./features/xp-events/xp-events.component').then((m) => m.XpEventsComponent),
      },
      {
        path: 'audit-logs',
        loadComponent: () => import('./features/audit-logs/audit-logs.component').then((m) => m.AuditLogsComponent),
      },
      {
        path: 'settings',
        loadComponent: () => import('./features/settings/settings.component').then((m) => m.SettingsComponent),
      },

      // ── Redirects from the pre-refactor flat IA ──
      // `content-tree` joins them: the screen was two levels deep and both already had one, so it
      // was folded into the Courses grid and its language filter (ADR-0047). It was in the sidebar
      // for the whole engagement, so its URL is bookmarked — a `**` bounce to the dashboard would
      // drop an author somewhere they did not ask for, when the destination they wanted is exactly
      // the screen that absorbed it.
      { path: 'content-tree', redirectTo: 'courses', pathMatch: 'full' },
      { path: 'learning-paths', redirectTo: 'courses', pathMatch: 'full' },
      { path: 'modules', redirectTo: 'courses', pathMatch: 'full' },
      { path: 'activities', redirectTo: 'courses', pathMatch: 'full' },
      { path: 'words', redirectTo: 'lexicon', pathMatch: 'full' },
      { path: 'packs', redirectTo: 'publishing', pathMatch: 'full' },
      { path: 'users', redirectTo: 'admins', pathMatch: 'full' },
      { path: 'gamification-config', redirectTo: 'gamification', pathMatch: 'full' },
    ],
  },
  { path: '**', redirectTo: '' },
];
